<template>
  <b-container fluid>
    <b-row>
      <b-navbar type="dark" variant="primary" toggleable="md" id="context-nav" style="width: 100%;">
        <b-navbar-toggle target="nav_dropdown_collapse"></b-navbar-toggle>
        <b-collapse is-nav id="nav_dropdown_collapse">
          <b-navbar-nav>
            <b-nav-item>
              <b-button @click="showCreateAutomation" size="sm">
                new automation
              </b-button>
            </b-nav-item>
            <b-nav-item v-if="activeAutomation">
              <b-button class="context-nav-danger" size="sm" @click="deleteAutomationModalVisible = true">
                delete
              </b-button>
            </b-nav-item>
            <b-nav-item>
              <modal-help :topic="automationHelpTopic" label="Help for this screen" show-label />
            </b-nav-item>
          </b-navbar-nav>
        </b-collapse>
      </b-navbar>
    </b-row>

    <b-row style="height: calc(100vh - 100px);">
      <b-col id="automation-list-col" cols="3">
        <div class="sidebar">
          <div class="sidebar-section-label">Automations</div>
          <button
            type="button"
            v-for="(automation, automationIndex) in automations"
            :key="automation.uniqueTitle || automationIndex"
            class="sidebar-item"
            :class="{ active: activeAutomation && activeAutomation.uniqueTitle === automation.uniqueTitle }"
            @click="chooseAutomation(automation)"
          >
            <font-awesome-icon icon="bolt" class="sidebar-icon" />
            <span class="sidebar-label">{{ automation.title || automation.uniqueTitle || 'untitled' }}</span>
            <span class="sidebar-child-type">{{ automationTypeShort(automation.type) }}</span>
          </button>
          <div v-if="!automations || automations.length === 0" class="sidebar-empty">
            No automations yet
          </div>
        </div>
      </b-col>

      <b-col cols="9" class="automation-content-col">
        <!-- Header -->
        <div class="content-header" v-if="activeAutomation">
          <label class="automation-title-field">
            <span class="visually-hidden">Automation title</span>
            <input v-model="activeAutomation.title" class="content-title-input" />
          </label>
          <span v-if="showsSeparateId(activeAutomation.title, activeAutomation.uniqueTitle)" class="content-unique-title">{{ activeAutomation.uniqueTitle }}</span>
          <span v-if="automationDirty" class="unsaved-mark">unsaved</span>
        </div>

        <!-- HTTP Request Editor -->
        <div v-if="activeAutomation && activeAutomation.type === 'http_request'" class="automation-editor">

          <!-- URL Bar -->
          <div class="url-bar">
            <b-form-select
              v-model="activeAutomation.config.method"
              :options="httpMethods"
              class="method-select"
              size="sm"
            />
            <b-form-input
              v-model="activeAutomation.config.url"
              placeholder="https://api.example.com/endpoint"
              class="url-input"
              size="sm"
            />
            <b-button
              variant="success"
              size="sm"
              @click="executeAutomation"
              :disabled="executing || !activeAutomation.config.url"
              class="send-btn"
            >
              {{ executing ? 'Sending...' : 'Send' }}
            </b-button>
            <b-button
              size="sm"
              @click="saveAutomation"
              class="save-btn"
              :disabled="saving"
            >
              {{ saveButtonLabel }}
            </b-button>
          </div>

          <!-- Config Tabs -->
          <div class="config-tabs">
            <span
              class="config-tab"
              :class="{ active: activeTab === 'params' }"
              @click="activeTab = 'params'"
            >
              Params
              <span class="tab-count" v-if="enabledParamsCount">({{ enabledParamsCount }})</span>
            </span>
            <span
              class="config-tab"
              :class="{ active: activeTab === 'headers' }"
              @click="activeTab = 'headers'"
            >
              Headers
              <span class="tab-count" v-if="enabledHeadersCount">({{ enabledHeadersCount }})</span>
            </span>
            <span
              class="config-tab"
              :class="{ active: activeTab === 'body' }"
              @click="activeTab = 'body'"
            >
              Body
            </span>
            <span
              class="config-tab"
              :class="{ active: activeTab === 'response' }"
              @click="activeTab = 'response'"
            >
              Save response
            </span>
            <span
              class="config-tab"
              :class="{ active: activeTab === 'history' }"
              @click="activeTab = 'history'; loadLogs()"
            >
              History
              <span class="tab-count" v-if="logs.length">({{ logs.length }})</span>
            </span>
          </div>

            <p class="integration-note">A variable name replaces the value when a form, a schedule, or a test run supplies it.</p>
          <div v-if="activeTab === 'params'" class="tab-content">
            <div class="kv-table">
              <div class="kv-row kv-header-row">
                <div class="kv-check"></div>
                <div class="kv-key">Key</div>
                <div class="kv-value">Value</div>
                <div class="kv-variable">Variable Name</div>
                <div class="kv-actions"></div>
              </div>
              <div v-for="(pair, index) in activeAutomation.config.queryParams" :key="'p' + index" class="kv-row">
                <div class="kv-check">
                  <input type="checkbox" v-model="pair.enabled" />
                </div>
                <div class="kv-key">
                  <b-form-input v-model="pair.key" placeholder="key" size="sm" class="kv-input" />
                </div>
                <div class="kv-value">
                  <b-form-input v-model="pair.value" placeholder="value" size="sm" class="kv-input" />
                </div>
                <div class="kv-variable">
                  <b-form-input v-model="pair.variableName" placeholder="variable name" size="sm" class="kv-input kv-variable-input" />
                </div>
                <div class="kv-actions">
                  <button type="button" @click="activeAutomation.config.queryParams.splice(index, 1)" class="kv-delete" aria-label="Remove">&times;</button>
                </div>
              </div>
              <button type="button" class="kv-add" @click="activeAutomation.config.queryParams.push({ key: '', value: '', enabled: true, variableName: '' })">+ add param</button>
            </div>
          </div>

          <!-- Headers Tab -->
          <div v-if="activeTab === 'headers'" class="tab-content">
            <div class="kv-table">
              <div class="kv-row kv-header-row">
                <div class="kv-check"></div>
                <div class="kv-key">Key</div>
                <div class="kv-value">Value</div>
                <div class="kv-variable">Variable Name</div>
                <div class="kv-actions"></div>
              </div>
              <div v-for="(pair, index) in activeAutomation.config.headers" :key="'h' + index" class="kv-row">
                <div class="kv-check">
                  <input type="checkbox" v-model="pair.enabled" />
                </div>
                <div class="kv-key">
                  <b-form-input v-model="pair.key" placeholder="key" size="sm" class="kv-input" />
                </div>
                <div class="kv-value">
                  <b-form-input v-model="pair.value" placeholder="value" size="sm" class="kv-input" />
                </div>
                <div class="kv-variable">
                  <b-form-input v-model="pair.variableName" placeholder="variable name" size="sm" class="kv-input kv-variable-input" />
                </div>
                <div class="kv-actions">
                  <button type="button" @click="activeAutomation.config.headers.splice(index, 1)" class="kv-delete" aria-label="Remove">&times;</button>
                </div>
              </div>
              <button type="button" class="kv-add" @click="activeAutomation.config.headers.push({ key: '', value: '', enabled: true, variableName: '' })">+ add header</button>
            </div>
          </div>

          <!-- Body Tab -->
          <div v-if="activeTab === 'body'" class="tab-content">
            <div class="body-type-selector">
              <label v-for="bodyType in bodyTypes" :key="bodyType" class="body-type-option">
                <input type="radio" :value="bodyType" v-model="activeAutomation.config.body.type" />
                <span>{{ bodyType }}</span>
              </label>
            </div>
            <div v-if="activeAutomation.config.body.type === 'json'" class="body-editor-wrap">
              <textarea
                v-model="activeAutomation.config.body.raw"
                class="body-editor"
                placeholder='{ "key": "value" }'
                spellcheck="false"
              ></textarea>
            </div>
            <div v-if="activeAutomation.config.body.type === 'form-data'" class="kv-table">
              <div class="kv-row kv-header-row">
                <div class="kv-check"></div>
                <div class="kv-key">Key</div>
                <div class="kv-value">Value</div>
                <div class="kv-variable">Variable Name</div>
                <div class="kv-actions"></div>
              </div>
              <div v-for="(pair, index) in activeAutomation.config.body.formData" :key="'f' + index" class="kv-row">
                <div class="kv-check">
                  <input type="checkbox" v-model="pair.enabled" />
                </div>
                <div class="kv-key">
                  <b-form-input v-model="pair.key" placeholder="key" size="sm" class="kv-input" />
                </div>
                <div class="kv-value">
                  <b-form-input v-model="pair.value" placeholder="value" size="sm" class="kv-input" />
                </div>
                <div class="kv-variable">
                  <b-form-input v-model="pair.variableName" placeholder="variable name" size="sm" class="kv-input kv-variable-input" />
                </div>
                <div class="kv-actions">
                  <button type="button" @click="activeAutomation.config.body.formData.splice(index, 1)" class="kv-delete" aria-label="Remove">&times;</button>
                </div>
              </div>
              <button type="button" class="kv-add" @click="activeAutomation.config.body.formData.push({ key: '', value: '', enabled: true, variableName: '' })">+ add field</button>
            </div>
            <div v-if="activeAutomation.config.body.type === 'none'" class="body-none-msg">
              This request does not have a body.
            </div>
          </div>

          <div v-if="activeTab === 'response'" class="tab-content">
            <label class="integration-check">
              <input type="checkbox" v-model="activeAutomation.config.responseMap.enabled" />
              Save the JSON response into a spreadsheet
            </label>
            <div v-if="activeAutomation.config.responseMap.enabled" class="integration-fields">
              <label class="automation-field">Save into
                <b-form-select v-model="activeAutomation.config.responseMap.target" :options="responseTargets" size="sm" />
              </label>
              <label class="automation-field">Spreadsheet
                <b-form-select
                  v-model="activeAutomation.config.responseMap.spreadSheetUniqueTitle"
                  :options="sheetOptions(activeAutomation.config.responseMap.spreadSheetUniqueTitle)"
                  size="sm"
                />
              </label>
              <b-form-select
                v-if="activeAutomation.config.responseMap.target !== 'trigger_record'"
                v-model="activeAutomation.config.responseMap.operation"
                :options="operationOptions"
                size="sm"
              />
              <b-form-select
                v-if="activeAutomation.config.responseMap.target !== 'trigger_record' && activeAutomation.config.responseMap.operation !== 'create'"
                v-model="activeAutomation.config.responseMap.matchColumn"
                :options="matchColumnOptions(activeAutomation.config.responseMap.spreadSheetUniqueTitle)"
                size="sm"
              />
              <p v-if="activeAutomation.config.responseMap.target === 'trigger_record'" class="integration-note">
                A form submission updates the record it just saved. Pick that record's spreadsheet so the column list matches. An array response is rejected.
              </p>
              <p v-else class="integration-note">
                An array of objects writes one row each, up to 100. Paths are read from each object.
              </p>
              <div class="kv-table">
                <div class="kv-row kv-header-row">
                  <div class="kv-key">Column</div>
                  <div class="kv-value">JSON path</div>
                  <div class="kv-actions"></div>
                </div>
                <div v-for="(entry, index) in activeAutomation.config.responseMap.columnMap" :key="'rm' + index" class="kv-row">
                  <div class="kv-key">
                    <b-form-select v-model="entry.columnUniqueTitle" :options="columnsFor(activeAutomation.config.responseMap.spreadSheetUniqueTitle)" size="sm" class="kv-input" />
                  </div>
                  <div class="kv-value">
                    <b-form-input v-model="entry.jsonPath" placeholder="customer.email" size="sm" class="kv-input" />
                  </div>
                  <div class="kv-actions">
                    <button type="button" @click="activeAutomation.config.responseMap.columnMap.splice(index, 1)" class="kv-delete" aria-label="Remove">&times;</button>
                  </div>
                </div>
                <button type="button" class="kv-add" @click="addResponseColumn()">+ add column</button>
              </div>
            </div>
          </div>

          <!-- History Tab -->
          <div v-if="activeTab === 'history'" class="tab-content">
            <div v-if="logsLoading" class="history-loading">Loading...</div>
            <div v-if="!logsLoading && logs.length === 0" class="history-empty">No execution history yet.</div>
            <button type="button" v-for="logItem in logs" :key="logItem._id" class="log-entry" @click="viewLog(logItem)">
              <span class="log-status" :class="logItem.status">{{ logItem.response && logItem.response.status ? logItem.response.status : 'ERR' }}</span>
              <span class="log-method">{{ logItem.request && logItem.request.method }}</span>
              <span class="log-url">{{ logItem.request && logItem.request.url }}</span>
              <span class="log-duration">{{ logItem.duration }}ms</span>
              <span class="log-time">{{ formatTime(logItem.executedAt) }}</span>
            </button>
          </div>

          <!-- Response Panel -->
          <div v-if="lastResponse" class="response-panel">
            <div class="response-meta">
              <span class="response-label">Response</span>
              <span
                class="response-status-badge"
                :class="lastResponse.response && lastResponse.response.status >= 400 ? 'error' : 'success'"
              >
                {{ lastResponse.response && lastResponse.response.status }} {{ lastResponse.response && lastResponse.response.statusText }}
              </span>
              <span class="response-duration">{{ lastResponse.duration }}ms</span>
              <span class="response-size">{{ formatSize(lastResponse.response && lastResponse.response.size) }}</span>
              <span v-if="lastResponse.response && lastResponse.response.truncated" class="response-truncated">(truncated)</span>
            </div>
            <div class="response-tabs">
              <span
                class="response-tab"
                :class="{ active: responseTab === 'body' }"
                @click="responseTab = 'body'"
              >Body</span>
              <span
                class="response-tab"
                :class="{ active: responseTab === 'headers' }"
                @click="responseTab = 'headers'"
              >Headers</span>
            </div>
            <pre v-if="responseTab === 'body'" class="response-body">{{ formatResponseBody(lastResponse.response && lastResponse.response.body) }}</pre>
            <div v-if="responseTab === 'headers'" class="response-headers-list">
              <div
                v-for="(value, key) in (lastResponse.response && lastResponse.response.headers)"
                :key="key"
                class="response-header-row"
              >
                <span class="header-key">{{ key }}</span>: <span class="header-value">{{ value }}</span>
              </div>
            </div>
          </div>

          <!-- Error display -->
          <div v-if="executeError" class="execute-error">
            {{ executeError }}
          </div>
        </div>

        <!-- Playwright Editor -->
        <div v-if="activeAutomation && activeAutomation.type === 'playwright'" class="automation-editor">

          <!-- Top Bar -->
          <div class="url-bar">
            <span class="pw-label">Start URL</span>
            <b-form-input
              v-model="activeAutomation.config.startUrl"
              placeholder="https://example.com"
              class="url-input"
              size="sm"
            />
            <b-button
              variant="success"
              size="sm"
              @click="executeAutomation"
              :disabled="executing || !activeAutomation.config.steps.length"
              class="send-btn"
            >
              {{ executing ? 'Running...' : 'Run' }}
            </b-button>
            <b-button
              size="sm"
              @click="saveAutomation"
              class="save-btn"
              :disabled="saving"
            >
              {{ saveButtonLabel }}
            </b-button>
          </div>

          <!-- Config Tabs -->
          <div class="config-tabs">
            <span
              class="config-tab"
              :class="{ active: playwrightActiveTab === 'steps' }"
              @click="playwrightActiveTab = 'steps'"
            >
              Steps
              <span class="tab-count" v-if="activeAutomation.config.steps.length">({{ activeAutomation.config.steps.length }})</span>
            </span>
            <span
              class="config-tab"
              :class="{ active: playwrightActiveTab === 'settings' }"
              @click="playwrightActiveTab = 'settings'"
            >
              Settings
            </span>
            <span
              class="config-tab"
              :class="{ active: playwrightActiveTab === 'history' }"
              @click="playwrightActiveTab = 'history'; loadLogs()"
            >
              History
              <span class="tab-count" v-if="logs.length">({{ logs.length }})</span>
            </span>
          </div>

          <!-- Steps Tab -->
          <div v-if="playwrightActiveTab === 'steps'" class="tab-content">
            <div class="kv-table">
              <div class="kv-row kv-header-row">
                <div class="kv-check"></div>
                <div class="pw-step-num">#</div>
                <div class="pw-step-type">Action</div>
                <div class="pw-step-fields">Configuration</div>
                <div class="kv-actions"></div>
              </div>

              <div v-for="(step, index) in activeAutomation.config.steps" :key="'s' + index" class="kv-row pw-step-row">
                <div class="kv-check">
                  <input type="checkbox" v-model="step.enabled" />
                </div>
                <div class="pw-step-num">
                  <span class="pw-step-num-label">{{ index + 1 }}</span>
                </div>
                <div class="pw-step-type">
                  <b-form-select
                    v-model="step.type"
                    :options="playwrightStepTypes"
                    size="sm"
                    class="kv-input"
                  />
                </div>
                <div class="pw-step-fields">
                  <b-form-input v-if="step.type === 'navigate'" v-model="step.url" placeholder="https://..." size="sm" class="kv-input" />
                  <b-form-input v-if="step.type === 'click'" v-model="step.selector" placeholder="CSS selector" size="sm" class="kv-input" />
                  <div v-if="step.type === 'type'" class="pw-multi-field">
                    <b-form-input v-model="step.selector" placeholder="CSS selector" size="sm" class="kv-input" />
                    <b-form-input v-model="step.value" placeholder="text to type" size="sm" class="kv-input" />
                  </div>
                  <div v-if="step.type === 'select'" class="pw-multi-field">
                    <b-form-input v-model="step.selector" placeholder="CSS selector" size="sm" class="kv-input" />
                    <b-form-input v-model="step.value" placeholder="option value" size="sm" class="kv-input" />
                  </div>
                  <b-form-input v-if="step.type === 'wait'" v-model="step.duration" placeholder="1000" size="sm" class="kv-input" type="number" aria-label="Wait milliseconds" />
                  <span v-if="step.type === 'wait'" class="integration-note">milliseconds</span>
                  <b-form-input v-if="step.type === 'screenshot'" v-model="step.name" placeholder="screenshot name (optional)" size="sm" class="kv-input" />
                  <div v-if="step.type === 'assert_text'" class="pw-multi-field">
                    <b-form-input v-model="step.selector" placeholder="CSS selector" size="sm" class="kv-input" />
                    <b-form-input v-model="step.expectedText" placeholder="expected text" size="sm" class="kv-input" />
                  </div>
                  <b-form-input v-if="step.type === 'assert_visible'" v-model="step.selector" placeholder="CSS selector" size="sm" class="kv-input" />
                </div>
                <div class="kv-actions">
                  <button type="button" @click="removePlaywrightStep(index)" class="kv-delete" aria-label="Remove">&times;</button>
                </div>
              </div>

              <button type="button" class="kv-add" @click="addPlaywrightStep()">+ add step</button>
            </div>
          </div>

          <!-- Settings Tab -->
          <div v-if="playwrightActiveTab === 'settings'" class="tab-content">
            <div class="pw-settings">
              <div class="pw-setting-row">
                <label class="pw-setting-label">Viewport Width</label>
                <b-form-input v-model.number="activeAutomation.config.viewport.width" type="number" size="sm" class="pw-setting-input" />
              </div>
              <div class="pw-setting-row">
                <label class="pw-setting-label">Viewport Height</label>
                <b-form-input v-model.number="activeAutomation.config.viewport.height" type="number" size="sm" class="pw-setting-input" />
              </div>
              <div class="pw-setting-row">
                <label class="pw-setting-label">Headless Mode</label>
                <input type="checkbox" v-model="activeAutomation.config.headless" style="accent-color: #9ad12b;" />
              </div>
            </div>
          </div>

          <!-- History Tab -->
          <div v-if="playwrightActiveTab === 'history'" class="tab-content">
            <div v-if="logsLoading" class="history-loading">Loading...</div>
            <div v-if="!logsLoading && logs.length === 0" class="history-empty">No execution history yet.</div>
            <button type="button" v-for="logItem in logs" :key="logItem._id" class="log-entry" @click="viewLog(logItem)">
              <span class="log-status" :class="logItem.status">{{ logItem.status === 'success' ? 'PASS' : 'FAIL' }}</span>
              <span class="log-method">PLAYWRIGHT</span>
              <span class="log-url">{{ logItem.request && logItem.request.url }}</span>
              <span class="log-duration">{{ logItem.duration }}ms</span>
              <span class="log-time">{{ formatTime(logItem.executedAt) }}</span>
            </button>
          </div>

          <!-- Results Panel -->
          <div v-if="lastResponse && lastResponse.playwrightResults" class="response-panel">
            <div class="response-meta">
              <span class="response-label">Results</span>
              <span
                class="response-status-badge"
                :class="lastResponse.status === 'success' ? 'success' : 'error'"
              >
                {{ lastResponse.status === 'success' ? 'PASSED' : 'FAILED' }}
              </span>
              <span class="response-duration">{{ lastResponse.duration }}ms</span>
              <span class="response-size">{{ lastResponse.playwrightResults.stepResults.length }} steps</span>
            </div>

            <div class="pw-results-list">
              <div
                v-for="(stepResult, srIndex) in lastResponse.playwrightResults.stepResults"
                :key="'sr' + srIndex"
                class="pw-step-result"
              >
                <span class="pw-step-result-num">{{ stepResult.index + 1 }}</span>
                <span class="pw-step-result-type">{{ stepResult.type }}</span>
                <span class="pw-step-result-status" :class="stepResult.status">
                  {{ stepResult.status === 'success' ? 'PASS' : 'FAIL' }}
                </span>
                <span class="pw-step-result-duration">{{ stepResult.duration }}ms</span>
                <span v-if="stepResult.error" class="pw-step-result-error">{{ stepResult.error }}</span>
              </div>
            </div>

            <div v-if="lastResponse.playwrightResults.screenshots && Object.keys(lastResponse.playwrightResults.screenshots).length" class="pw-screenshots">
              <div class="pw-screenshots-label">Screenshots</div>
              <div v-for="(base64, name) in lastResponse.playwrightResults.screenshots" :key="name" class="pw-screenshot-item">
                <div class="pw-screenshot-name">{{ name }}</div>
                <img :src="'data:image/png;base64,' + base64" class="pw-screenshot-img" />
              </div>
            </div>
          </div>

          <!-- Fallback for history items without playwrightResults -->
          <div v-if="lastResponse && !lastResponse.playwrightResults" class="response-panel">
            <div class="response-meta">
              <span class="response-label">Results</span>
              <span class="response-status-badge" :class="lastResponse.status === 'success' ? 'success' : 'error'">
                {{ lastResponse.status === 'success' ? 'PASSED' : 'FAILED' }}
              </span>
              <span class="response-duration">{{ lastResponse.duration }}ms</span>
            </div>
            <pre class="response-body">{{ formatResponseBody(lastResponse.response && lastResponse.response.body) }}</pre>
          </div>

          <!-- Error display -->
          <div v-if="executeError" class="execute-error">
            {{ executeError }}
          </div>
        </div>

        <div v-if="activeAutomation && activeAutomation.type === 'send_email'" class="automation-editor">
          <div class="url-bar">
            <b-button variant="success" size="sm" @click="executeAutomation" :disabled="executing" class="send-btn">
              {{ executing ? 'Sending...' : 'Send' }}
            </b-button>
            <b-button size="sm" @click="toggleHistory" class="save-btn">History</b-button>
            <b-button size="sm" @click="saveAutomation" class="save-btn" :disabled="saving">
              {{ saveButtonLabel }}
            </b-button>
          </div>
          <div class="tab-content integration-fields">
            <p class="integration-note">
              Placeholders look like &#123;&#123;email&#125;&#125;. A form or schedule can fill them. Accounts are saved in
              <router-link :to="`/${$route.params.application}/settings?tab=mail`">app settings</router-link>.
            </p>
            <label class="automation-field">Mail account
              <b-form-select v-model="activeAutomation.config.mailAccountId" :options="mailAccountOptions" size="sm" aria-label="Mail account" />
            </label>
            <p v-if="selectedMailAccount" class="integration-note">
              Sends as {{ selectedMailAccount.from || 'an unset from address' }} through {{ selectedMailAccount.host || 'an unset host' }}.
            </p>
            <p v-else-if="activeAutomation.config.mailAccountId" class="integration-note">
              That email account is no longer in app settings.
            </p>
            <label class="automation-field">To<b-form-input v-model="activeAutomation.config.to" placeholder="to, {{email}}" size="sm" /></label>
            <label class="automation-field">Subject<b-form-input v-model="activeAutomation.config.subject" placeholder="subject" size="sm" /></label>
            <label class="automation-field">Message<b-form-textarea v-model="activeAutomation.config.body" placeholder="message" rows="6" /></label>
            <label class="automation-field">Test variables<b-form-textarea v-model="testVariablesText" placeholder='{"email":"ada@example.com"}' rows="3" /></label>
          </div>
          <p v-if="runSummary" class="run-summary" :class="lastResponse && lastResponse.status">{{ runSummary }}</p>
          <div v-if="executeError" class="execute-error">{{ executeError }}</div>
          <div v-if="historyOpen" class="tab-content">
            <div v-if="logsLoading" class="history-loading">Loading...</div>
            <div v-if="!logsLoading && logs.length === 0" class="history-empty">No execution history yet.</div>
            <button type="button" v-for="logItem in logs" :key="logItem._id" class="log-entry" @click="viewLog(logItem)">
              <span class="log-status" :class="logItem.status">{{ logItem.status }}</span>
              <span class="log-url">{{ summarizeRun(logItem) }}</span>
              <span class="log-time">{{ formatTime(logItem.executedAt) }}</span>
            </button>
          </div>
          <pre v-if="integrationResult" class="response-body">{{ integrationResult }}</pre>
        </div>

        <div v-if="activeAutomation && activeAutomation.type === 'write_records'" class="automation-editor">
          <div class="url-bar">
            <b-button variant="success" size="sm" @click="executeAutomation" :disabled="executing" class="send-btn">
              {{ executing ? 'Running...' : 'Run' }}
            </b-button>
            <b-button size="sm" @click="toggleHistory" class="save-btn">History</b-button>
            <b-button size="sm" @click="saveAutomation" class="save-btn" :disabled="saving">
              {{ saveButtonLabel }}
            </b-button>
          </div>
          <div class="tab-content integration-fields">
            <label class="automation-field">Spreadsheet
              <b-form-select v-model="activeAutomation.config.spreadSheetUniqueTitle" :options="sheetOptions(activeAutomation.config.spreadSheetUniqueTitle)" size="sm" />
            </label>
            <label class="automation-field">Operation
              <b-form-select v-model="activeAutomation.config.operation" :options="operationOptions" size="sm" />
            </label>
            <label v-if="activeAutomation.config.operation !== 'create'" class="automation-field">Match column
              <b-form-select
                v-model="activeAutomation.config.matchColumn"
                :options="matchColumnOptions(activeAutomation.config.spreadSheetUniqueTitle)"
                size="sm"
              />
            </label>
            <div class="kv-table">
              <div class="kv-row kv-header-row">
                <div class="kv-key">Column</div>
                <div class="kv-value">From</div>
                <div class="kv-variable">Value</div>
                <div class="kv-actions"></div>
              </div>
              <div v-for="(entry, index) in activeAutomation.config.columnMap" :key="'wc' + index" class="kv-row">
                <div class="kv-key">
                  <b-form-select v-model="entry.columnUniqueTitle" :options="columnsFor(activeAutomation.config.spreadSheetUniqueTitle)" size="sm" class="kv-input" />
                </div>
                <div class="kv-value">
                  <b-form-select v-model="entry.source" :options="valueSources" size="sm" class="kv-input" />
                </div>
                <div class="kv-variable">
                  <b-form-input v-if="entry.source === 'variable'" v-model="entry.variableName" placeholder="variable name" size="sm" class="kv-input" />
                  <b-form-input v-else v-model="entry.staticValue" placeholder="value or {{name}}" size="sm" class="kv-input" />
                </div>
                <div class="kv-actions">
                  <button type="button" @click="activeAutomation.config.columnMap.splice(index, 1)" class="kv-delete" aria-label="Remove">&times;</button>
                </div>
              </div>
              <button type="button" class="kv-add" @click="addWriteColumn()">+ add column</button>
            </div>
            <label class="automation-field">Test variables
              <b-form-textarea v-model="testVariablesText" placeholder='{"email":"ada@example.com"}' rows="3" />
            </label>
          </div>
          <p v-if="runSummary" class="run-summary" :class="lastResponse && lastResponse.status">{{ runSummary }}</p>
          <div v-if="executeError" class="execute-error">{{ executeError }}</div>
          <div v-if="historyOpen" class="tab-content">
            <div v-if="logsLoading" class="history-loading">Loading...</div>
            <div v-if="!logsLoading && logs.length === 0" class="history-empty">No execution history yet.</div>
            <button type="button" v-for="logItem in logs" :key="logItem._id" class="log-entry" @click="viewLog(logItem)">
              <span class="log-status" :class="logItem.status">{{ logItem.status }}</span>
              <span class="log-url">{{ summarizeRun(logItem) }}</span>
              <span class="log-time">{{ formatTime(logItem.executedAt) }}</span>
            </button>
          </div>
          <pre v-if="integrationResult" class="response-body">{{ integrationResult }}</pre>
        </div>

        <div v-if="activeAutomation && activeAutomation.type === 'webhook'" class="automation-editor">
          <div class="url-bar">
            <b-button variant="success" size="sm" @click="executeAutomation" :disabled="executing" class="send-btn">
              {{ executing ? 'Running...' : 'Test' }}
            </b-button>
            <b-button size="sm" @click="toggleHistory" class="save-btn">History</b-button>
            <b-button size="sm" @click="saveAutomation" class="save-btn" :disabled="saving">
              {{ saveButtonLabel }}
            </b-button>
          </div>
          <div class="tab-content integration-fields">
            <p class="integration-note">POST JSON to this URL with the secret header. Test runs the sample below without the secret.</p>
            <label class="automation-field">URL
              <b-form-input :model-value="webhookUrl" readonly size="sm" class="webhook-url" />
            </label>
            <label class="automation-field">Secret header
              <b-form-input v-model="activeAutomation.config.secretHeader" placeholder="X-Webhook-Secret" size="sm" />
            </label>
            <label class="automation-field">Secret
              <b-form-input v-model="activeAutomation.config.secret" placeholder="secret" type="password" size="sm" />
            </label>
            <div class="kv-table">
              <div class="kv-row kv-header-row">
                <div class="kv-key">JSON path</div>
                <div class="kv-value">Variable</div>
                <div class="kv-actions"></div>
              </div>
              <div v-for="(field, index) in activeAutomation.config.fieldMap" :key="'wf' + index" class="kv-row">
                <div class="kv-key">
                  <b-form-input v-model="field.jsonPath" placeholder="customer.email" size="sm" class="kv-input" />
                </div>
                <div class="kv-value">
                  <b-form-input v-model="field.variableName" placeholder="email" size="sm" class="kv-input" />
                </div>
                <div class="kv-actions">
                  <button type="button" @click="activeAutomation.config.fieldMap.splice(index, 1)" class="kv-delete" aria-label="Remove">&times;</button>
                </div>
              </div>
              <button type="button" class="kv-add" @click="addWebhookField()">+ add variable</button>
            </div>
            <p class="integration-note">Optional. Leave the spreadsheet blank to only record the JSON.</p>
            <label class="automation-field">Spreadsheet
              <b-form-select v-model="activeAutomation.config.write.spreadSheetUniqueTitle" :options="sheetOptions(activeAutomation.config.write.spreadSheetUniqueTitle)" size="sm" />
            </label>
            <label class="automation-field">Operation
              <b-form-select v-model="activeAutomation.config.write.operation" :options="operationOptions" size="sm" />
            </label>
            <label v-if="activeAutomation.config.write.operation !== 'create'" class="automation-field">Match column
              <b-form-select
                v-model="activeAutomation.config.write.matchColumn"
                :options="matchColumnOptions(activeAutomation.config.write.spreadSheetUniqueTitle)"
                size="sm"
              />
            </label>
            <div class="kv-table">
              <div class="kv-row kv-header-row">
                <div class="kv-key">Column</div>
                <div class="kv-value">From</div>
                <div class="kv-variable">Value</div>
                <div class="kv-actions"></div>
              </div>
              <div v-for="(entry, index) in activeAutomation.config.write.columnMap" :key="'whc' + index" class="kv-row">
                <div class="kv-key">
                  <b-form-select v-model="entry.columnUniqueTitle" :options="columnsFor(activeAutomation.config.write.spreadSheetUniqueTitle)" size="sm" class="kv-input" />
                </div>
                <div class="kv-value">
                  <b-form-select v-model="entry.source" :options="webhookSources" size="sm" class="kv-input" />
                </div>
                <div class="kv-variable">
                  <b-form-input v-if="entry.source === 'json'" v-model="entry.jsonPath" placeholder="customer.email" size="sm" class="kv-input" />
                  <b-form-input v-else-if="entry.source === 'variable'" v-model="entry.variableName" placeholder="email" size="sm" class="kv-input" />
                  <b-form-input v-else v-model="entry.staticValue" placeholder="value" size="sm" class="kv-input" />
                </div>
                <div class="kv-actions">
                  <button type="button" @click="activeAutomation.config.write.columnMap.splice(index, 1)" class="kv-delete" aria-label="Remove">&times;</button>
                </div>
              </div>
              <button type="button" class="kv-add" @click="addWebhookColumn()">+ add column</button>
            </div>
            <label class="automation-field">Sample JSON
              <b-form-textarea v-model="webhookSample" placeholder="sample JSON" rows="5" />
            </label>
          </div>
          <p v-if="runSummary" class="run-summary" :class="lastResponse && lastResponse.status">{{ runSummary }}</p>
          <div v-if="executeError" class="execute-error">{{ executeError }}</div>
          <div v-if="historyOpen" class="tab-content">
            <div v-if="logsLoading" class="history-loading">Loading...</div>
            <div v-if="!logsLoading && logs.length === 0" class="history-empty">No execution history yet.</div>
            <button type="button" v-for="logItem in logs" :key="logItem._id" class="log-entry" @click="viewLog(logItem)">
              <span class="log-status" :class="logItem.status">{{ logItem.status }}</span>
              <span class="log-url">{{ summarizeRun(logItem) }}</span>
              <span class="log-time">{{ formatTime(logItem.executedAt) }}</span>
            </button>
          </div>
          <pre v-if="integrationResult" class="response-body">{{ integrationResult }}</pre>
        </div>

        <!-- Empty state -->
        <div v-if="!activeAutomation" class="empty-state">
          Select an automation or create a new one
        </div>
      </b-col>
    </b-row>

    <!-- Create automation modal -->
    <b-modal v-model="createAutomationModalVisible" title="create new automation" @ok="createAutomation">
      <template #title>
        create new automation
        <modal-help topic="create-automation" />
      </template>
      <b-container fluid>
        <b-form>
          <b-row>
            <b-form-group label="automation title">
              <b-form-input v-model="newAutomationTitle" placeholder="my new automation name" required @input="onTitleInput" />
            </b-form-group>
          </b-row>
          <b-row>
            <b-form-group label="unique title">
              <b-form-input v-model="newAutomationUniqueTitle" placeholder="my_new_automation_name" required @input="newAutomationUniqueTitleManuallyEdited = true" />
            </b-form-group>
          </b-row>
          <b-row>
            <b-form-group label="automation type">
              <b-form-select v-model="selectedType" :options="typeOptions" class="mb-3" size="sm" required />
              <p class="integration-note">{{ automationTypeHelp(selectedType) }}</p>
            </b-form-group>
          </b-row>
        </b-form>
        <div v-if="createError" class="create-error">{{ createError }}</div>
      </b-container>
    </b-modal>

    <!-- Delete automation modal -->
    <b-modal v-model="deleteAutomationModalVisible" title="delete automation" @ok="deleteAutomation" ok-variant="danger" ok-title="delete">
      <template #title>
        delete automation
        <modal-help topic="delete-automation" />
      </template>
      <p v-if="activeAutomation">Delete <strong>{{ activeAutomation.title }}</strong> ({{ activeAutomation.uniqueTitle }})? This also deletes its execution history.</p>
      <div v-if="automationUses.length">
        <p>It is used by:</p>
        <ul>
          <li v-for="(use, index) in automationUses" :key="index">
            <template v-if="use.kind === 'form'">{{ use.name }} on {{ use.page }}</template>
            <template v-else>schedule {{ use.name }}</template>
          </li>
        </ul>
      </div>
    </b-modal>
  </b-container>
</template>

<script>
  import axios from 'axios'
  import {
    AUTOMATION_TYPE_OPTIONS,
    ensureAutomationConfig,
    automationTypeLabel as labelForAutomationType,
    automationTypeHelp as helpForAutomationType,
    automationTypeShort as shortForAutomationType,
    summarizeAutomationRun,
    automationUsage,
    webhookPath,
    writableColumns
  } from '../../../../shared/automation-integration.js'
  import { showsSeparateId } from '../../lib/designer-labels.js'
  import { onDesignerChange, raiseDesignerWarning } from '../../lib/designer-sync.js'

  export default {
    data () {

      return {
        automations: [],
        activeAutomation: null,
        activeTab: 'params',
        responseTab: 'body',
        lastResponse: null,
        executing: false,
        saving: false,
        executeError: '',
        logs: [],
        logsLoading: false,
        mailAccounts: [],
        mailDefaultAccountId: '',

        // create modal
        createAutomationModalVisible: false,
        newAutomationTitle: '',
        newAutomationUniqueTitle: '',
        newAutomationUniqueTitleManuallyEdited: false,
        selectedType: 'http_request',
        typeOptions: AUTOMATION_TYPE_OPTIONS,
        createError: '',
        savedSnapshot: '',
        savedFlash: false,
        historyOpen: false,
        automationUses: [],
        testVariablesText: '',
        webhookSample: '{\n  "email": "ada@example.com"\n}',
        operationOptions: [
          { value: 'create', text: 'Create' },
          { value: 'update', text: 'Update' },
          { value: 'upsert', text: 'Upsert' }
        ],
        responseTargets: [
          { value: 'spreadsheet', text: 'Save into a spreadsheet' },
          { value: 'trigger_record', text: 'Update the submitted record' }
        ],
        valueSources: [
          { value: 'variable', text: 'Variable' },
          { value: 'static', text: 'Static' }
        ],
        webhookSources: [
          { value: 'json', text: 'JSON path' },
          { value: 'variable', text: 'Variable' },
          { value: 'static', text: 'Static' }
        ],

        // delete modal
        deleteAutomationModalVisible: false,

        httpMethods: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'],
        bodyTypes: ['none', 'json', 'form-data'],

        // playwright
        playwrightActiveTab: 'steps',
        playwrightStepTypes: [
          { value: 'navigate', text: 'Navigate' },
          { value: 'click', text: 'Click' },
          { value: 'type', text: 'Type' },
          { value: 'select', text: 'Select' },
          { value: 'wait', text: 'Wait' },
          { value: 'screenshot', text: 'Screenshot' },
          { value: 'assert_text', text: 'Assert Text' },
          { value: 'assert_visible', text: 'Assert Visible' }
        ]
      }
    },
    computed: {
      automationHelpTopic () {

        const types = {
          http_request: 'automation-http',
          playwright: 'automation-playwright',
          send_email: 'automation-email',
          write_records: 'automation-write',
          webhook: 'automation-webhook'
        }

        if (!this.activeAutomation) return 'automation-editor'

        return types[this.activeAutomation.type] || 'automation-editor'
      },
      enabledParamsCount () {

        if (!this.activeAutomation || !this.activeAutomation.config || !this.activeAutomation.config.queryParams) return 0

        return this.activeAutomation.config.queryParams.filter(p => p.enabled && p.key).length
      },
      enabledHeadersCount () {

        if (!this.activeAutomation || !this.activeAutomation.config || !this.activeAutomation.config.headers) return 0

        return this.activeAutomation.config.headers.filter(h => h.enabled && h.key).length
      },
      webhookUrl () {

        if (!this.activeAutomation || this.activeAutomation.type !== 'webhook') return ''

        const api = typeof VUE_APP_PROXY_SERVER_URL !== 'undefined' ? VUE_APP_PROXY_SERVER_URL : ''

        return `${api}${webhookPath(this.$route.params.application, this.activeAutomation.uniqueTitle)}`
      },
      integrationResult () {

        if (!this.lastResponse || !this.activeAutomation) return ''
        if (!['send_email', 'write_records', 'webhook'].includes(this.activeAutomation.type)) return ''

        return JSON.stringify(this.lastResponse, null, 2)
      },
      runSummary () {

        return summarizeAutomationRun(this.lastResponse)
      },
      saveButtonLabel () {

        if (this.saving) return 'Saving...'
        if (this.savedFlash) return 'Saved'

        return 'Save'
      },
      automationDirty () {

        if (!this.activeAutomation || !this.savedSnapshot) return false

        return this.automationSnapshot() !== this.savedSnapshot
      },
      mailAccountOptions () {

        return this.mailAccountChoices()
      },
      selectedMailAccount () {

        if (!this.activeAutomation || this.activeAutomation.type !== 'send_email') return null

        const id = this.activeAutomation.config.mailAccountId || this.mailDefaultAccountId

        return this.mailAccounts.find(account => account.id === id) || null
      }
    },
    mounted () {

      this.getAutomations()
      this.getSpreadsheets()
      this.loadMailAccounts()
      this._stopDesignerSync = onDesignerChange((change) => this.applyDesignerChange(change))
    },
    beforeUnmount () {

      if (this._stopDesignerSync) this._stopDesignerSync()
    },
    watch: {
      '$route.params.application' () {
        this.automations = []
        this.activeAutomation = null
        this.savedSnapshot = ''
        this.getAutomations()
        this.getSpreadsheets()
        this.loadMailAccounts()
      },
      deleteAutomationModalVisible (visible) {

        if (visible) this.loadAutomationUses()
      }
    },
    beforeRouteLeave (to, from, next) {

      if (!this.confirmLeave()) {

        next(false)
        return
      }

      next()
    },
    methods: {

      showsSeparateId,
      mailAccountChoices () {

        const options = this.mailAccounts.map(account => ({
          value: account.id,
          text: account.id === this.mailDefaultAccountId ? `${account.name} (default)` : account.name
        }))
        const selected = this.activeAutomation && this.activeAutomation.config && this.activeAutomation.config.mailAccountId

        if (selected && !this.mailAccounts.some(account => account.id === selected)) {

          options.unshift({ value: selected, text: 'Missing account' })
        }

        if (!this.mailAccounts.length) {

          return selected ? [{ value: selected, text: 'Missing account' }] : [{ value: '', text: 'No email accounts yet' }]
        }

        const fallback = this.mailAccounts.find(account => account.id === this.mailDefaultAccountId) || this.mailAccounts[0]

        return [{ value: '', text: `Default · ${fallback.name}` }].concat(options)
      },
      async loadMailAccounts () {

        try {

          const res = await axios.get(`${VUE_APP_PROXY_SERVER_URL}/settings`, {
            headers: { 'application-unique-title': this.$route.params.application }
          })
          const mail = (res.data && res.data.mail) || {}

          this.mailAccounts = Array.isArray(mail.accounts) ? mail.accounts : []
          this.mailDefaultAccountId = mail.defaultAccountId || (this.mailAccounts[0] && this.mailAccounts[0].id) || ''
        } catch (err) {

          this.mailAccounts = []
          this.mailDefaultAccountId = ''
        }
      },

      automationTypeLabel (type) {

        return labelForAutomationType(type)
      },

      automationTypeHelp (type) {

        return helpForAutomationType(type)
      },

      automationTypeShort (type) {

        return shortForAutomationType(type)
      },

      summarizeRun (entry) {

        return summarizeAutomationRun(entry)
      },

      automationSnapshot () {

        if (!this.activeAutomation) return ''

        return JSON.stringify({
          title: this.activeAutomation.title,
          config: this.activeAutomation.config
        })
      },

      markSaved () {

        this.savedSnapshot = this.automationSnapshot()
      },

      rememberSnapshot () {

        this.markSaved()
        this.savedFlash = true
        setTimeout(() => { this.savedFlash = false }, 2000)
      },

      confirmLeave () {

        if (!this.automationDirty) return true

        return window.confirm('This automation has unsaved changes. Leave without saving?')
      },

      chooseAutomation (automation) {

        if (this.activeAutomation && automation.uniqueTitle === this.activeAutomation.uniqueTitle) return
        if (!this.confirmLeave()) return

        this.loadAutomation(automation)
      },

      toggleHistory () {

        this.historyOpen = !this.historyOpen
        if (this.historyOpen) this.loadLogs()
      },

      async getSpreadsheets () {

        try {

          const response = await this.$http.get(
            `${VUE_APP_PROXY_SERVER_URL}/spreadsheets/`,
            { headers: { 'application-unique-title': this.$route.params.application } }
          )

          this.spreadsheets = response.data || []
        } catch (error) {

          console.log('error fetching spreadsheets:', error)
          this.spreadsheets = []
        }
      },

      sheetOptions (selected) {

        const options = [{ value: '', text: 'spreadsheet' }].concat((this.spreadsheets || []).map((sheet) => ({
          value: sheet.uniqueTitle,
          text: sheet.title || sheet.uniqueTitle
        })))

        if (selected && !options.some((option) => option.value === selected)) {

          options.push({ value: selected, text: selected })
        }

        return options
      },

      columnsFor (sheetUniqueTitle) {

        const sheet = (this.spreadsheets || []).find((item) => item.uniqueTitle === sheetUniqueTitle)
        const columns = writableColumns(sheet && sheet.columns)

        return [{ value: '', text: 'column' }].concat(columns.map((column) => ({
          value: column.uniqueTitle,
          text: column.title || column.uniqueTitle
        })))
      },

      matchColumnOptions (sheetUniqueTitle) {

        return [{ value: '', text: 'match column' }, { value: 'id', text: 'ID' }].concat(
          this.columnsFor(sheetUniqueTitle).filter((option) => option.value)
        )
      },

      addWriteColumn () {

        this.activeAutomation.config.columnMap.push({
          columnUniqueTitle: '',
          source: 'variable',
          variableName: '',
          staticValue: ''
        })
      },

      addWebhookField () {

        this.activeAutomation.config.fieldMap.push({ jsonPath: '', variableName: '' })
      },

      addWebhookColumn () {

        this.activeAutomation.config.write.columnMap.push({
          columnUniqueTitle: '',
          source: 'json',
          jsonPath: '',
          variableName: '',
          staticValue: ''
        })
      },

      addResponseColumn () {

        this.activeAutomation.config.responseMap.columnMap.push({ columnUniqueTitle: '', jsonPath: '' })
      },

      executePayload () {

        const payload = { trigger: 'manual' }
        const type = this.activeAutomation && this.activeAutomation.type

        if ((type === 'send_email' || type === 'write_records') && this.testVariablesText.trim()) {

          payload.variables = JSON.parse(this.testVariablesText)
        }

        if (type === 'webhook') {

          payload.body = this.webhookSample.trim() ? JSON.parse(this.webhookSample) : {}
        }

        return payload
      },

      async refreshAutomationListKeepingActive () {

        const response = await this.$http.get(
          `${VUE_APP_PROXY_SERVER_URL}/automations/`,
          { headers: { 'application-unique-title': this.$route.params.application } }
        )
        const id = this.activeAutomation && this.activeAutomation.uniqueTitle
        const rows = response.data || []

        this.automations = rows.map((automation) => automation.uniqueTitle === id ? this.activeAutomation : automation)

        if (id && !this.automations.some((automation) => automation.uniqueTitle === id)) {

          this.automations.push(this.activeAutomation)
        }
      },
      async applyDesignerChange (change) {

        const id = this.activeAutomation && this.activeAutomation.uniqueTitle
        const keys = change && change.keys || []
        const hitsActive = !!(id && (keys.includes(`automation:${id}`) || keys.includes('application')))
        const hitsList = keys.includes('automations') || keys.includes('application') || keys.some((key) => key.startsWith('automation:'))

        if (!hitsList && !hitsActive) return

        if (this.automationDirty && hitsActive) {

          raiseDesignerWarning(change, keys.includes('application') ? 'this application' : 'this automation')
          await this.refreshAutomationListKeepingActive()

          return
        }

        if (this.automationDirty) {

          await this.refreshAutomationListKeepingActive()

          return
        }

        await this.getAutomations()
      },

      // CRUD

      async getAutomations () {

        try {

          const response = await this.$http.get(
            `${VUE_APP_PROXY_SERVER_URL}/automations/`,
            { headers: { 'application-unique-title': this.$route.params.application } }
          )

          this.automations = response.data

          // deep link support
          const targetUniqueTitle = this.$route.params.automation

          if (targetUniqueTitle) {

            const target = this.automations.find(s => s.uniqueTitle === targetUniqueTitle)

            if (target) {

              this.loadAutomation(target)
            }
          }
        } catch (error) {

          console.log('error fetching automations:', error)
        }
      },

      loadAutomation (automation) {

        this.activeAutomation = automation
        this.lastResponse = null
        this.logs = []
        this.executeError = ''
        this.activeTab = 'params'
        this.responseTab = 'body'

        ensureAutomationConfig(this.activeAutomation)

        if (this.activeAutomation.type === 'playwright') {

          this.playwrightActiveTab = 'steps'
        }

        // update URL for deep linking
        const targetPath = `/${this.$route.params.application}/automations/${automation.uniqueTitle}`

        if (this.$route.path !== targetPath) {

          this.$router.replace(targetPath)
        }

        this.historyOpen = false
        this.markSaved()
      },

      showCreateAutomation () {

        this.newAutomationTitle = ''
        this.newAutomationUniqueTitle = ''
        this.newAutomationUniqueTitleManuallyEdited = false
        this.selectedType = 'http_request'
        this.createError = ''
        this.createAutomationModalVisible = true
      },

      onTitleInput () {

        if (!this.newAutomationUniqueTitleManuallyEdited) {

          this.newAutomationUniqueTitle = this.sanitizeToUniqueTitle(this.newAutomationTitle)
        }
      },

      async createAutomation () {

        this.createError = ''

        if (!this.newAutomationTitle || !this.newAutomationUniqueTitle) {

          this.createError = 'title and unique title are required'
          return
        }

        try {

          await this.$http.post(
            `${VUE_APP_PROXY_SERVER_URL}/automations/`,
            {
              title: this.newAutomationTitle,
              uniqueTitle: this.newAutomationUniqueTitle,
              type: this.selectedType
            },
            { headers: { 'application-unique-title': this.$route.params.application } }
          )

          await this.getAutomations()

          // select the newly created automation
          const newAutomation = this.automations.find(s => s.uniqueTitle === this.newAutomationUniqueTitle)

          if (newAutomation) {

            this.loadAutomation(newAutomation)
          }
        } catch (error) {

          this.createError = error.response ? error.response.data : error.message
          console.log('error creating automation:', error)
        }
      },

      async saveAutomation () {

        if (!this.activeAutomation) return

        this.saving = true

        try {

          const response = await this.$http.put(
            `${VUE_APP_PROXY_SERVER_URL}/automations/${this.activeAutomation.uniqueTitle}`,
            {
              title: this.activeAutomation.title,
              config: this.activeAutomation.config
            },
            { headers: { 'application-unique-title': this.$route.params.application } }
          )

          // update the automation in the list
          const index = this.automations.findIndex(s => s.uniqueTitle === this.activeAutomation.uniqueTitle)

          if (index !== -1) {

            this.automations[index] = response.data
          }

          this.activeAutomation = response.data
          this.rememberSnapshot()
        } catch (error) {

          console.log('error saving automation:', error)
        }

        this.saving = false
      },

      async loadAutomationUses () {

        this.automationUses = []
        if (!this.activeAutomation) return

        try {

          const headers = { 'application-unique-title': this.$route.params.application }
          const [pages, schedules] = await Promise.all([
            this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/pages/`, { headers }),
            this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/schedules/`, { headers })
          ])

          this.automationUses = automationUsage(this.activeAutomation.uniqueTitle, pages.data, schedules.data)
        } catch (error) {

          console.log('error loading automation usage:', error)
        }
      },

      async deleteAutomation () {

        if (!this.activeAutomation) return

        try {

          await this.$http.delete(
            `${VUE_APP_PROXY_SERVER_URL}/automations/${this.activeAutomation.uniqueTitle}`,
            { headers: { 'application-unique-title': this.$route.params.application } }
          )

          this.activeAutomation = null
          this.lastResponse = null
          this.logs = []

          await this.getAutomations()

          this.$router.replace(`/${this.$route.params.application}/automations`)
        } catch (error) {

          console.log('error deleting automation:', error)
        }
      },

      // Execution

      async executeAutomation () {

        if (!this.activeAutomation) return

        this.executing = true
        this.executeError = ''
        this.lastResponse = null

        try {

          // save first, then execute
          await this.saveAutomation()

          const response = await this.$http.post(
            `${VUE_APP_PROXY_SERVER_URL}/automations/${this.activeAutomation.uniqueTitle}/execute`,
            this.executePayload(),
            { headers: { 'application-unique-title': this.$route.params.application } }
          )

          this.lastResponse = response.data
          this.responseTab = 'body'
        } catch (error) {

          this.executeError = error.response ? error.response.data : error.message
          console.log('error executing automation:', error)
        }

        this.executing = false
      },

      // Logs

      async loadLogs () {

        if (!this.activeAutomation) return

        this.logsLoading = true

        try {

          const response = await this.$http.get(
            `${VUE_APP_PROXY_SERVER_URL}/automations/${this.activeAutomation.uniqueTitle}/logs`,
            { headers: { 'application-unique-title': this.$route.params.application } }
          )

          this.logs = response.data
        } catch (error) {

          console.log('error loading logs:', error)
        }

        this.logsLoading = false
      },

      viewLog (logItem) {

        this.lastResponse = logItem
        this.responseTab = 'body'

        if (this.activeAutomation && this.activeAutomation.type === 'playwright') {

          this.playwrightActiveTab = 'steps'
        } else {

          this.activeTab = 'params'
        }
      },

      // Playwright helpers

      addPlaywrightStep () {

        this.activeAutomation.config.steps.push({
          type: 'navigate',
          url: '',
          selector: '',
          value: '',
          duration: 1000,
          name: '',
          expectedText: '',
          enabled: true
        })
      },

      removePlaywrightStep (index) {

        this.activeAutomation.config.steps.splice(index, 1)
      },

      // Utility

      sanitizeToUniqueTitle (title) {

        return title.toLowerCase().replace(/[^a-z0-9\s_]/g, '').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '')
      },

      formatTime (dateString) {

        return new Date(dateString).toLocaleString()
      },

      formatSize (bytes) {

        if (!bytes) return '0 B'
        if (bytes < 1024) return bytes + ' B'
        return (bytes / 1024).toFixed(1) + ' KB'
      },

      formatResponseBody (body) {

        if (!body) return ''

        try {

          return JSON.stringify(JSON.parse(body), null, 2)
        } catch (e) {

          return body
        }
      }
    }
  }
</script>

<style>

#context-nav {
  background-color: #181915 !important;
  border-bottom: 1px #2e2e2b solid;
}

#automation-list-col {
  padding: 0 !important;
}

.automation-content-col {
  padding: 0 !important;
  overflow-y: auto;
}

.sidebar {
  background-color: #141412;
  border-right: 1px solid #2e2e2b;
  height: 100%;
  overflow-y: auto;
  padding-bottom: 32px;
}

.sidebar-section-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a8a8a4;
  padding: 14px 14px 8px;
}

.sidebar-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  cursor: pointer;
  color: #b0b0ae;
  font-size: 0.875rem;
  transition: background 0.1s, color 0.1s;
  user-select: none;
}
.sidebar-item:hover {
  background-color: #1e1e1b;
  color: #e0e0de;
}
.sidebar-item.active {
  background-color: #1a1a17;
  color: #e8e8e6;
  border-left: 2px solid #9ad12b;
  padding-left: 12px;
}

.sidebar-icon {
  font-size: 0.75rem;
  color: #4a4a47;
  flex-shrink: 0;
}

.sidebar-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-child-type {
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 0.7rem;
  color: #a8a8a4;
  font-family: monospace;
}

.sidebar-empty {
  padding: 12px 14px;
  color: #a8a8a4;
  font-size: 0.8rem;
  font-style: italic;
}

.content-header {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 14px 18px 10px;
  border-bottom: 1px solid #2e2e2b;
}
.content-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #e8e8e6;
}
.content-unique-title {
  font-size: 0.8rem;
  color: #9ad12b;
  font-family: monospace;
}
.content-title-input {
  background: transparent;
  border: 0;
  border-bottom: 1px solid transparent;
  color: #e8e8e6;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0;
  min-width: 12rem;
}
.content-title-input:focus {
  outline: none;
  border-bottom-color: #3a3a37;
}
.unsaved-mark {
  color: #c9a227;
  font-size: 0.75rem;
}
.automation-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #9aa0a6;
  font-size: 0.8rem;
}
.run-summary {
  margin: 8px 18px;
}
.run-summary.success { color: #9ad12b; }
.run-summary.error { color: #f0a0a0; }
button.sidebar-item {
  width: 100%;
  border: 0;
  background: transparent;
  font: inherit;
  text-align: left;
}
button.kv-add,
button.kv-delete {
  background: none;
  border: 0;
  font: inherit;
  padding: 0;
}

/* Delete button */
.delete {
  color: #f82471 !important;
  border: 1px #f82471 solid;
  background: transparent;
  transition: background 0.15s, color 0.15s;
}
.delete:hover {
  background-color: #f82471;
  color: #fff !important;
}

/* Automation Editor */
.automation-editor {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 160px);
}

/* URL Bar */
.url-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #2e2e2b;
  align-items: center;
  flex-shrink: 0;
}
.method-select {
  max-width: 110px;
  background-color: #1a1a17 !important;
  color: #9ad12b !important;
  border-color: #2e2e2b !important;
  font-weight: 600;
  font-size: 0.8rem;
}
.url-input {
  flex: 1 1 160px;
  min-width: 0;
  background-color: #1a1a17 !important;
  color: #e8e8e6 !important;
  border-color: #2e2e2b !important;
  font-size: 0.85rem;
}
.url-input::placeholder {
  color: #3e3e3c !important;
}
.send-btn {
  background-color: #9ad12b !important;
  color: #0f0f0d !important;
  border: none !important;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 5px 16px;
  white-space: nowrap;
}
.send-btn:disabled {
  opacity: 0.5;
}
.save-btn {
  background-color: #2a2a27 !important;
  color: #b0b0ae !important;
  border: 1px solid #3e3e3c !important;
  font-size: 0.8rem;
  padding: 5px 14px;
  white-space: nowrap;
}
.save-btn:hover {
  background-color: #3e3e3c !important;
  color: #e8e8e6 !important;
}

/* Config Tabs */
.config-tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid #2e2e2b;
  padding: 0 16px;
  flex-shrink: 0;
}
.config-tab {
  padding: 8px 14px;
  cursor: pointer;
  color: #a8a8a4;
  font-size: 0.8rem;
  border-bottom: 2px solid transparent;
  transition: color 0.1s, border-color 0.1s;
  user-select: none;
}
.config-tab:hover {
  color: #b0b0ae;
}
.config-tab.active {
  color: #e8e8e6;
  border-bottom-color: #9ad12b;
}
.tab-count {
  font-size: 0.7rem;
  color: #9ad12b;
}

/* Tab Content */
.tab-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* Key-Value Editor */
.kv-table {
  padding: 8px 16px;
}
.kv-row {
  display: flex;
  gap: 8px;
  padding: 3px 0;
  align-items: center;
}
.kv-header-row {
  font-size: 0.7rem;
  font-weight: 600;
  color: #a8a8a4;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding-bottom: 6px;
  border-bottom: 1px solid #1e1e1b;
  margin-bottom: 4px;
}
.kv-check {
  width: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kv-check input[type="checkbox"] {
  accent-color: #9ad12b;
}
.kv-key {
  flex: 1;
}
.kv-value {
  flex: 1;
}
.kv-variable {
  flex: 0.8;
}
.kv-variable-input::placeholder {
  color: #4a4a47 !important;
  font-style: italic;
}
.kv-actions {
  width: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kv-delete {
  color: #f82471;
  cursor: pointer;
  font-weight: bold;
  font-size: 1.1rem;
  line-height: 1;
}
.kv-delete:hover {
  color: #ff5a9e;
}
.kv-add {
  color: #9ad12b;
  cursor: pointer;
  font-size: 0.8rem;
  padding: 8px 0 4px;
  user-select: none;
}
.kv-add:hover {
  color: #b8f044;
}
.kv-input {
  background-color: #1a1a17 !important;
  color: #e8e8e6 !important;
  border-color: #2e2e2b !important;
  font-size: 0.8rem;
}
.kv-input::placeholder {
  color: #3e3e3c !important;
}

/* Body Tab */
.body-type-selector {
  display: flex;
  gap: 16px;
  padding: 10px 16px;
  border-bottom: 1px solid #1e1e1b;
}
.body-type-option {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #b0b0ae;
  font-size: 0.8rem;
  cursor: pointer;
  user-select: none;
}
.body-type-option input[type="radio"] {
  accent-color: #9ad12b;
}
.body-editor-wrap {
  padding: 8px 16px;
  flex: 1;
}
.body-editor {
  width: 100%;
  min-height: 200px;
  background-color: #1a1a17;
  color: #e8e8e6;
  border: 1px solid #2e2e2b;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.8rem;
  padding: 12px;
  resize: vertical;
  outline: none;
}
.body-editor:focus {
  border-color: #9ad12b;
}
.body-none-msg {
  padding: 16px;
  color: #a8a8a4;
  font-size: 0.8rem;
  font-style: italic;
}

/* Response Panel */
.response-panel {
  border-top: 2px solid #2e2e2b;
  flex-shrink: 0;
  max-height: 50%;
  display: flex;
  flex-direction: column;
}
.response-meta {
  display: flex;
  gap: 14px;
  padding: 10px 16px;
  align-items: center;
  font-size: 0.8rem;
  flex-shrink: 0;
}
.response-label {
  color: #a8a8a4;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.05em;
}
.response-status-badge.success {
  color: #9ad12b;
  font-weight: 600;
}
.response-status-badge.error {
  color: #f82471;
  font-weight: 600;
}
.response-duration {
  color: #a8a8a4;
}
.response-size {
  color: #a8a8a4;
}
.response-truncated {
  color: #f0a500;
  font-size: 0.7rem;
}
.response-tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid #2e2e2b;
  padding: 0 16px;
  flex-shrink: 0;
}
.response-tab {
  padding: 6px 12px;
  cursor: pointer;
  color: #a8a8a4;
  font-size: 0.75rem;
  border-bottom: 2px solid transparent;
  transition: color 0.1s, border-color 0.1s;
  user-select: none;
}
.response-tab:hover {
  color: #b0b0ae;
}
.response-tab.active {
  color: #e8e8e6;
  border-bottom-color: #9ad12b;
}
.response-body {
  background-color: #0f0f0d;
  color: #e8e8e6;
  padding: 12px 16px;
  font-family: monospace;
  font-size: 0.75rem;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  flex: 1;
  min-height: 0;
}
.response-headers-list {
  padding: 8px 16px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}
.response-header-row {
  font-size: 0.75rem;
  padding: 2px 0;
  font-family: monospace;
  color: #b0b0ae;
}
.header-key {
  color: #9ad12b;
  font-weight: 600;
}
.header-value {
  color: #e8e8e6;
}

/* History/Logs */
.history-loading, .history-empty {
  padding: 16px;
  color: #a8a8a4;
  font-size: 0.8rem;
  font-style: italic;
}
.log-entry {
  display: flex;
  gap: 12px;
  width: 100%;
  padding: 8px 16px;
  cursor: pointer;
  font: inherit;
  font-size: 0.8rem;
  border: 0;
  border-bottom: 1px solid #1e1e1b;
  background: transparent;
  color: #b0b0ae;
  text-align: left;
  align-items: center;
  transition: background 0.1s;
}
.log-entry:hover {
  background-color: #1e1e1b;
}
.log-status {
  font-weight: 600;
  min-width: 36px;
}
.log-status.success {
  color: #9ad12b;
}
.log-status.error {
  color: #f82471;
}
.log-method {
  color: #e8e8e6;
  font-weight: 600;
  min-width: 55px;
  font-family: monospace;
  font-size: 0.75rem;
}
.log-url {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: monospace;
  font-size: 0.75rem;
}
.log-duration {
  color: #a8a8a4;
  min-width: 55px;
  text-align: right;
}
.log-time {
  color: #3e3e3c;
  font-size: 0.7rem;
  min-width: 130px;
  text-align: right;
}

/* Empty state */
.integration-fields {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 760px;
}

.integration-note {
  margin: 0;
  color: #9aa0a6;
  font-size: 13px;
}

.integration-check {
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-control.webhook-url:read-only {
  color: #e8e8e6;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #a8a8a4;
  font-size: 0.9rem;
  font-style: italic;
}

/* Error displays */
.execute-error {
  padding: 10px 16px;
  background-color: #2a1015;
  color: #f82471;
  font-size: 0.8rem;
  border-top: 1px solid #f82471;
}
.create-error {
  color: #f82471;
  font-size: 0.8rem;
  margin-top: 8px;
}

/* Playwright Editor */
.pw-label {
  color: #a8a8a4;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.pw-step-num {
  width: 30px;
  flex-shrink: 0;
  text-align: center;
}
.pw-step-num-label {
  color: #a8a8a4;
  font-size: 0.75rem;
  font-family: monospace;
}

.pw-step-type {
  width: 130px;
  flex-shrink: 0;
}

.pw-step-fields {
  flex: 1;
}

.pw-step-row {
  padding: 5px 0;
}

.pw-multi-field {
  display: flex;
  gap: 6px;
}

/* Playwright Settings */
.pw-settings {
  padding: 12px 16px;
}
.pw-setting-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
}
.pw-setting-label {
  color: #b0b0ae;
  font-size: 0.8rem;
  min-width: 120px;
}
.pw-setting-input {
  max-width: 120px;
  background-color: #1a1a17 !important;
  color: #e8e8e6 !important;
  border-color: #2e2e2b !important;
  font-size: 0.8rem;
}

/* Playwright Step Results */
.pw-results-list {
  padding: 0 16px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}
.pw-step-result {
  display: flex;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #1e1e1b;
  font-size: 0.8rem;
  align-items: center;
}
.pw-step-result-num {
  width: 24px;
  color: #a8a8a4;
  font-family: monospace;
  text-align: center;
}
.pw-step-result-type {
  min-width: 90px;
  color: #e8e8e6;
  font-family: monospace;
  font-size: 0.75rem;
}
.pw-step-result-status {
  font-weight: 600;
  min-width: 36px;
}
.pw-step-result-status.success {
  color: #9ad12b;
}
.pw-step-result-status.error {
  color: #f82471;
}
.pw-step-result-duration {
  color: #a8a8a4;
  min-width: 55px;
  text-align: right;
}
.pw-step-result-error {
  color: #f82471;
  font-size: 0.75rem;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Playwright Screenshots */
.pw-screenshots {
  border-top: 1px solid #2e2e2b;
  padding: 10px 16px;
}
.pw-screenshots-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #a8a8a4;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}
.pw-screenshot-item {
  margin-bottom: 12px;
}
.pw-screenshot-name {
  color: #9ad12b;
  font-size: 0.75rem;
  font-family: monospace;
  margin-bottom: 4px;
}
.pw-screenshot-img {
  max-width: 100%;
  border: 1px solid #2e2e2b;
  border-radius: 4px;
}
</style>
